import React, { useState, useEffect } from "react";
import { SAFE_ITEMS, EMERGENCY_STEPS, VET_CONTACTS } from "./mock";
import { APP } from "./constants/testIds";
import axios from "axios";
import { 
  ShieldAlert, Search, AlertTriangle, CheckCircle2, PhoneCall, 
  BookOpen, Compass, MessageSquare, HeartPulse, ChevronRight, 
  Leaf, X, Plus, Check, AlertOctagon, Sparkles, UserCheck, Filter, Globe,
  Camera, Upload, Loader2, ScanLine, ShieldCheck, Trash2, Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Named constants (replace magic numbers) ----
const IOS_INSTALL_HINT_DELAY_MS = 3500;
const CAMERA_FALLBACK_WIDTH = 640;
const CAMERA_FALLBACK_HEIGHT = 480;
const IMAGE_MAX_SIDE_PX = 1024;
const IMAGE_JPEG_QUALITY = 0.85;
const ANALYSIS_TIMEOUT_MS = 120000;

// ---- Small helpers to avoid nested ternaries in JSX ----
const severityBadgeClass = (severity) => {
  if (severity === "Critical") return "bg-red-600 text-white";
  if (severity === "High") return "bg-amber-600 text-white";
  return "bg-stone-700 text-white";
};

const simulatorItemClass = (item) => {
  if (!item.checked) return "bg-stone-50/50 border-stone-200 text-stone-600 hover:bg-stone-100";
  if (item.type === "toxic") return "bg-red-50/60 border-red-200 text-red-950";
  return "bg-emerald-50/60 border-emerald-200 text-emerald-950";
};

export default function App() {
  const [activeTab, setActiveTab] = useState("encyclopedia");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSeverity, setSelectedSeverity] = useState("All");
  const [selectedPlant, setSelectedPlant] = useState(null);

  // Database Data State
  const [toxicPlants, setToxicPlants] = useState([]);
  const [reports, setReports] = useState([]);
  const [loadingPlants, setLoadingPlants] = useState(true);
  const [recentScans, setRecentScans] = useState([]);

  // Pasture Simulator State
  const [simulatorItems, setSimulatorItems] = useState([
    { id: "sim1", name: "Rhododendron leaves near fence", type: "toxic", plantId: "1", checked: false },
    { id: "sim2", name: "Clean Timothy Grass Hay", type: "safe", plantId: null, checked: true },
    { id: "sim3", name: "Fallen Wilted Red Maple branches", type: "toxic", plantId: "3", checked: false },
    { id: "sim4", name: "Sliced fresh carrots", type: "safe", plantId: null, checked: true },
    { id: "sim5", name: "Discarded lawn mower grass clippings", type: "toxic", plantId: "10", checked: false },
    { id: "sim6", name: "Orchard grass pasture", type: "safe", plantId: null, checked: true }
  ]);
  const [customSimulatorInput, setCustomSimulatorInput] = useState("");
  const [simulatorResult, setSimulatorResult] = useState(null);

  // Community Report Form
  const [showReportModal, setShowReportModal] = useState(false);
  const [newReport, setNewReport] = useState({ author: "", location: "", title: "", content: "", severity: "High" });
  const [submittingReport, setSubmittingReport] = useState(false);

  // Plant Photo Scan
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanImagePreview, setScanImagePreview] = useState(null);
  const [scanImageB64, setScanImageB64] = useState(null);
  const [scanMime, setScanMime] = useState("image/jpeg");
  const [cameraActive, setCameraActive] = useState(false);
  const cameraStreamRef = React.useRef(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [scanError, setScanError] = useState(null);

  // PWA install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHint, setShowInstallHint] = useState(false);
  const [iosStandalone, setIosStandalone] = useState(false);

  useEffect(() => {
    // Android / Chrome / Edge fire this
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setInstallPrompt(null);
      setShowInstallHint(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    // iOS Safari detection (no beforeinstallprompt on iOS)
    const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIosStandalone(standalone);
    if (isIOS && !standalone) {
      // Show the iOS install hint after a delay, and only if the user hasn't dismissed before
      const dismissed = localStorage.getItem("alpacasafe-ios-install-dismissed");
      if (!dismissed) {
        const t = setTimeout(() => setShowInstallHint(true), IOS_INSTALL_HINT_DELAY_MS);
        return () => {
          clearTimeout(t);
          window.removeEventListener("beforeinstallprompt", onBeforeInstall);
          window.removeEventListener("appinstalled", onInstalled);
        };
      }
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch (err) {
      console.warn("Install prompt userChoice failed", err);
    }
    setInstallPrompt(null);
  };

  const dismissIosHint = () => {
    localStorage.setItem("alpacasafe-ios-install-dismissed", "1");
    setShowInstallHint(false);
  };

  // Fetch from FastAPI Backend
  useEffect(() => {
    async function fetchData() {
      try {
        const [plantsRes, reportsRes, scansRes] = await Promise.all([
          axios.get(`${API}/plants`),
          axios.get(`${API}/reports`),
          axios.get(`${API}/scans`).catch(() => ({ data: [] }))
        ]);
        setToxicPlants(plantsRes.data);
        setReports(reportsRes.data);
        setRecentScans(scansRes.data || []);
      } catch (e) {
        console.error("Error fetching backend data", e);
      } finally {
        setLoadingPlants(false);
      }
    }
    fetchData();
  }, []);

  // Handle PWA shortcut URL params (?action=scan or ?tab=emergency|reports|simulator)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    const action = params.get("action");
    if (tab && ["encyclopedia", "simulator", "emergency", "reports"].includes(tab)) {
      setActiveTab(tab);
    }
    if (action === "scan") {
      setActiveTab("encyclopedia");
      setTimeout(() => {
        setShowScanModal(true);
        setScanImagePreview(null);
        setScanImageB64(null);
        setAnalysisResult(null);
        setScanError(null);
      }, 400);
    }
    if (tab || action) {
      // Clean the URL so refresh doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete("tab");
      url.searchParams.delete("action");
      window.history.replaceState({}, "", url.pathname + (url.search || ""));
    }
  }, []);

  // Filtered Plants
  const filteredPlants = toxicPlants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          plant.scientificName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          plant.toxin.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || plant.category === selectedCategory;
    const matchesSeverity = selectedSeverity === "All" || plant.severity === selectedSeverity;
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const categories = ["All", "Shrubs & Trees", "Ornamental Plants", "Trees & Leaves", "Evergreens", "Trees & Nuts", "Ferns & Weeds", "Weeds & Garden Plants", "Wetland Weeds", "Foodstuffs & Fruit Trees", "Foodstuffs & Vegetables", "Bulbs & Corms", "Grasses & Grains", "Fungi & Insects", "Vines & Climbers", "Wildflowers"];
  const severities = ["All", "Critical", "High", "Medium"];

  // Google Translate Widget Integration Effect
  useEffect(() => {
    if (document.getElementById('google-translate-script')) return;
    window.googleTranslateInit = () => {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'en,es,fr,de,it,pt,nl,zh-CN,ja,ko,ar,ru,hi,tr,pl,sv',
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false
        }, 'google_translate_element');
      }
    };
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateInit';
    document.body.appendChild(script);
  }, []);

  const runSimulator = () => {
    const selected = simulatorItems.filter(i => i.checked);
    const toxicFound = selected.filter(i => i.type === "toxic");
    setSimulatorResult({
      totalChecked: selected.length,
      toxicFound: toxicFound.length,
      items: toxicFound
    });
  };

  const handleAddReport = async (e) => {
    e.preventDefault();
    if (!newReport.title || !newReport.author) return;
    setSubmittingReport(true);
    try {
      const res = await axios.post(`${API}/reports`, newReport);
      setReports([res.data, ...reports]);
      setNewReport({ author: "", location: "", title: "", content: "", severity: "High" });
      setShowReportModal(false);
    } catch (e) {
      console.error("Error creating report", e);
    } finally {
      setSubmittingReport(false);
    }
  };

  // --- Plant Photo Scan handlers ---
  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    setCameraActive(false);
  };

  const openScanner = () => {
    setScanImagePreview(null);
    setScanImageB64(null);
    setAnalysisResult(null);
    setScanError(null);
    setShowScanModal(true);
  };

  const closeScanner = () => {
    stopCamera();
    setShowScanModal(false);
    setScanImagePreview(null);
    setScanImageB64(null);
    setAnalysisResult(null);
    setScanError(null);
  };

  const startCamera = async () => {
    setScanError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraActive(true);
      // Wait for videoRef to render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (err) {
      setScanError("Cannot access camera. Please grant permission or use upload instead.");
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || CAMERA_FALLBACK_WIDTH;
    const h = v.videoHeight || CAMERA_FALLBACK_HEIGHT;
    // Downscale to max IMAGE_MAX_SIDE_PX on the longest side
    const scale = Math.min(1, IMAGE_MAX_SIDE_PX / Math.max(w, h));
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", IMAGE_JPEG_QUALITY);
    setScanImagePreview(dataUrl);
    setScanImageB64(dataUrl.split(",")[1]);
    setScanMime("image/jpeg");
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|jpg)$/i.test(file.type)) {
      setScanError("Please choose a JPEG, PNG, or WEBP image.");
      return;
    }
    setScanError(null);
    const reader = new FileReader();
    reader.onload = () => {
      // Optionally downscale
      const img = new Image();
      img.onload = () => {
        const maxSide = IMAGE_MAX_SIDE_PX;
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
        const dataUrl = c.toDataURL(mime, IMAGE_JPEG_QUALITY);
        setScanImagePreview(dataUrl);
        setScanImageB64(dataUrl.split(",")[1]);
        setScanMime(mime);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!scanImageB64) return;
    setAnalyzing(true);
    setScanError(null);
    setAnalysisResult(null);
    try {
      const res = await axios.post(`${API}/analyze-plant`, {
        image_base64: scanImageB64,
        mime_type: scanMime,
      }, { timeout: ANALYSIS_TIMEOUT_MS });
      setAnalysisResult(res.data);
      // Refresh recent scans
      try {
        const scansRes = await axios.get(`${API}/scans`);
        setRecentScans(scansRes.data || []);
      } catch (refreshErr) {
        console.error("Failed to refresh recent scans", refreshErr);
      }
    } catch (err) {
      console.error("Analyze error", err);
      setScanError(err?.response?.data?.detail || "Analysis failed. Please try another photo.");
    } finally {
      setAnalyzing(false);
    }
  };

  const openHistoricScan = (scan) => {
    setScanImagePreview(scan.thumbnail);
    setScanImageB64(null); // cannot re-analyze without full image, but preview works
    setAnalysisResult(scan.report);
    setScanError(null);
    setShowScanModal(true);
  };

  const deleteScan = async (scanId, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.delete(`${API}/scans/${scanId}`);
      setRecentScans(recentScans.filter(s => s.id !== scanId));
    } catch (err) {
      console.error("Delete scan error", err);
    }
  };

  const resetScan = () => {
    setScanImagePreview(null);
    setScanImageB64(null);
    setAnalysisResult(null);
    setScanError(null);
  };

  const riskColorClasses = (risk) => {
    const r = (risk || "").toLowerCase();
    if (r === "critical") return "bg-red-600 text-white border-red-700";
    if (r === "high") return "bg-orange-500 text-white border-orange-600";
    if (r === "medium") return "bg-amber-400 text-stone-900 border-amber-500";
    if (r === "low") return "bg-yellow-200 text-stone-900 border-yellow-300";
    if (r === "safe") return "bg-emerald-500 text-white border-emerald-600";
    return "bg-stone-400 text-white border-stone-500";
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] text-[#2A2825] flex flex-col selection:bg-[#B85D34] selection:text-white">
      {/* Top Emergency Banner */}
      <div className="bg-[#B85D34] text-white px-4 py-2 text-xs sm:text-sm font-medium flex flex-wrap justify-between items-center shadow-inner" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
        <div className="flex items-center gap-2 mx-auto sm:mx-0">
          <AlertOctagon className="w-4 h-4 animate-pulse text-amber-200 shrink-0" />
          <span className="truncate"><strong>Emergency Alpaca Poisoning?</strong> <span className="hidden sm:inline">Call National Animal Poison Control instantly: </span><a href="tel:8884264435" className="underline underline-offset-2 font-bold">(888) 426-4435</a></span>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs">
          <span>🌿 Camelid Safe Forage Standard</span>
          <span>🛡️ Vet Verified 2026 Guidelines</span>
        </div>
      </div>

      {/* iOS Install Hint (Add to Home Screen) */}
      {showInstallHint && (
        <div data-testid="ios-install-hint" className="bg-[#3B5333] text-white px-4 py-2 text-xs flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2">
            <ScanLine className="w-4 h-4 shrink-0" />
            <span>Install AlpacaSafe: tap <strong>Share</strong> → <strong>Add to Home Screen</strong></span>
          </span>
          <button
            data-testid="dismiss-ios-hint-btn"
            onClick={dismissIosHint}
            className="w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
            aria-label="Dismiss install hint"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Navigation Header */}
      <header className="sticky top-0 z-30 bg-[#F9F7F2]/95 backdrop-blur-md border-b border-[#E3DCD0] px-3 sm:px-8 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-3 cursor-pointer w-full sm:w-auto justify-between sm:justify-start" onClick={() => setActiveTab("encyclopedia")}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#3B5333] flex items-center justify-center text-white shadow-md shrink-0">
                <Leaf className="w-6 h-6 text-[#D4A373]" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold font-serif tracking-tight text-[#2A2825]">
                  Alpaca<span className="text-[#B85D34]">Safe</span>
                </h1>
                <p className="text-[10px] sm:text-xs text-stone-600 font-medium hidden sm:block">Toxic Plant & Foodstuffs Reference for Alpacas</p>
              </div>
            </div>
            {/* Mobile: install button beside logo */}
            {installPrompt && (
              <button
                data-testid="install-app-btn-mobile"
                onClick={(e) => { e.stopPropagation(); triggerInstall(); }}
                className="sm:hidden inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#D4A373] text-[#2A2825] text-xs font-bold shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" /> Install
              </button>
            )}
          </div>

          {/* Navigation Tabs (horizontally scrollable on mobile) */}
          <nav className="flex items-center gap-1 bg-[#EFECE6] p-1.5 rounded-2xl border border-[#E3DCD0] overflow-x-auto w-full sm:w-auto max-w-full">
            <button
              data-testid="nav-encyclopedia"
              onClick={() => setActiveTab("encyclopedia")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === "encyclopedia"
                  ? "bg-[#3B5333] text-white shadow-sm"
                  : "text-stone-700 hover:text-[#2A2825] hover:bg-stone-200/60"
              }`}
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden xs:inline sm:inline">Encyclopedia</span>
              <span className="xs:hidden sm:hidden">Guide</span>
            </button>
            <button
              data-testid="nav-simulator"
              onClick={() => setActiveTab("simulator")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === "simulator"
                  ? "bg-[#3B5333] text-white shadow-sm"
                  : "text-stone-700 hover:text-[#2A2825] hover:bg-stone-200/60"
              }`}
            >
              <Compass className="w-4 h-4" />
              <span className="hidden sm:inline">Pasture Simulator</span>
              <span className="sm:hidden">Pasture</span>
            </button>
            <button
              data-testid="nav-emergency"
              onClick={() => setActiveTab("emergency")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === "emergency"
                  ? "bg-[#3B5333] text-white shadow-sm"
                  : "text-stone-700 hover:text-[#2A2825] hover:bg-stone-200/60"
              }`}
            >
              <HeartPulse className="w-4 h-4" />
              <span className="hidden sm:inline">Emergency Care</span>
              <span className="sm:hidden">Emergency</span>
            </button>
            <button
              data-testid="nav-reports"
              onClick={() => setActiveTab("reports")}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap ${
                activeTab === "reports"
                  ? "bg-[#3B5333] text-white shadow-sm"
                  : "text-stone-700 hover:text-[#2A2825] hover:bg-stone-200/60"
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Vet Reports</span>
              <span className="sm:hidden">Reports</span>
            </button>
          </nav>

          <div className="flex items-center gap-2">
            {/* Desktop install button */}
            {installPrompt && (
              <button
                data-testid="install-app-btn"
                onClick={triggerInstall}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#D4A373] text-[#2A2825] text-xs font-bold shadow-sm hover:bg-[#c99163] transition-all"
              >
                <Plus className="w-3.5 h-3.5" /> Install App
              </button>
            )}
            {/* Language Switcher (Google Translate) */}
            <div
              data-testid="language-switcher"
              className="flex items-center gap-2 bg-[#EFECE6] border border-[#E3DCD0] rounded-xl px-3 py-1.5"
              title="Translate this page"
            >
              <Globe className="w-4 h-4 text-[#3B5333]" />
              <div id="google_translate_element" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-8 py-8">
        
        {/* ================= TAB 1: ENCYCLOPEDIA ================= */}
        {activeTab === "encyclopedia" && (
          <div className="space-y-8">
            {/* Hero Banner */}
            <div className="bg-gradient-to-r from-[#3B5333] to-[#2D4027] rounded-3xl p-6 sm:p-10 text-white relative overflow-hidden shadow-xl">
              <div className="absolute right-0 top-0 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none"></div>
              <div className="max-w-2xl relative z-10 space-y-4">
                <h2 className="text-3xl sm:text-4xl font-bold font-serif tracking-tight">
                  Protect Your Herd from Dangerous Plants & Foodstuffs
                </h2>
                <p className="text-stone-200 text-sm sm:text-base leading-relaxed">
                  Alpacas possess delicate 3-chambered forestomachs and unique metabolic sensitivities. Explore our verified database of toxic shrubs, weeds, trees, and improper foodstuffs to keep your pasture safe.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-xs font-medium">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    <span>{toxicPlants.length} Verified Toxic Hazards</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl text-xs font-medium">
                    <ShieldAlert className="w-4 h-4 text-emerald-400" />
                    <span>Camelid Specific Toxicity Scores</span>
                  </div>
                </div>
                <div className="pt-2">
                  <button
                    data-testid="open-plant-scanner-btn"
                    onClick={openScanner}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#D4A373] text-[#2A2825] font-semibold text-sm shadow-lg hover:bg-[#c99163] transition-all hover:-translate-y-0.5"
                  >
                    <ScanLine className="w-4 h-4" />
                    Scan a Plant Photo (AI Risk Analysis)
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Scans Strip */}
            {recentScans.length > 0 && (
              <div data-testid="recent-scans-strip" className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E3DCD0] shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#EAF0E8] text-[#3B5333] flex items-center justify-center">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-[#2A2825] text-base leading-none">Recent Scans</h3>
                      <p className="text-xs text-stone-500 mt-0.5">Tap a card to revisit its risk assessment</p>
                    </div>
                  </div>
                  <span className="text-xs text-stone-500 font-medium">{recentScans.length} saved</span>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                  {recentScans.map((s) => {
                    const risk = s.report?.overall_risk_level || "Unknown";
                    const top = s.report?.plants?.[0];
                    const topName = top?.common_name || "No plants identified";
                    return (
                      <button
                        key={s.id}
                        data-testid={`recent-scan-${s.id}`}
                        onClick={() => openHistoricScan(s)}
                        className="group relative flex-shrink-0 w-40 rounded-xl overflow-hidden border border-[#E3DCD0] bg-stone-50 hover:shadow-md transition-all text-left hover:-translate-y-0.5"
                      >
                        <div className="relative w-full h-24 bg-stone-200 overflow-hidden">
                          {s.thumbnail && (
                            <img src={s.thumbnail} alt="Scan" className="w-full h-full object-cover" />
                          )}
                          <span className={`absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${riskColorClasses(risk)}`}>
                            {risk}
                          </span>
                          <button
                            data-testid={`delete-scan-${s.id}`}
                            onClick={(e) => deleteScan(s.id, e)}
                            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-md bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500"
                            aria-label="Delete scan"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="px-2.5 py-2">
                          <div className="text-xs font-semibold text-[#2A2825] truncate">{topName}</div>
                          <div className="text-[10px] text-stone-500 mt-0.5">
                            {new Date(s.created_at).toLocaleDateString()} · {s.report?.plants?.length || 0} plant{(s.report?.plants?.length || 0) === 1 ? "" : "s"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search & Filters Bar */}
            <div className="bg-white p-4 sm:p-6 rounded-2xl border border-[#E3DCD0] shadow-sm space-y-4">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                {/* Search Input */}
                <div className="relative w-full md:w-96">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    data-testid={APP.searchInput}
                    type="text"
                    placeholder="Search plant, scientific name, or toxin..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#3B5333] text-sm bg-stone-50/50"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Severity Filter */}
                <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0">
                  <span className="text-xs font-semibold text-stone-500 whitespace-nowrap">Severity:</span>
                  {severities.map(sev => (
                    <button
                      key={sev}
                      data-testid={`${APP.severityFilter}-${sev.toLowerCase()}`}
                      onClick={() => setSelectedSeverity(sev)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                        selectedSeverity === sev 
                          ? "bg-[#B85D34] text-white shadow-sm" 
                          : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                      }`}
                    >
                      {sev}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Pills */}
              <div className="flex items-center gap-2 overflow-x-auto pt-2 border-t border-stone-100 pb-1">
                <span className="text-xs font-semibold text-stone-500 whitespace-nowrap flex items-center gap-1">
                  <Filter className="w-3.5 h-3.5" /> Category:
                </span>
                {categories.map(cat => (
                  <button
                    key={cat}
                    data-testid={`${APP.categoryFilter}-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all whitespace-nowrap ${
                      selectedCategory === cat 
                        ? "bg-[#3B5333] text-white shadow-sm" 
                        : "bg-stone-100/80 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Plant Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlants.map(plant => (
                <div
                  key={plant.id}
                  data-testid={`${APP.plantCard}-${plant.id}`}
                  onClick={() => setSelectedPlant(plant)}
                  className="bg-white rounded-2xl border border-[#E3DCD0] overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col group"
                >
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#EAF0E8] to-[#DDE7DA]">
                    <img
                      src={plant.image}
                      alt={plant.name}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.style.opacity = "0";
                        e.currentTarget.parentElement?.classList.add("plant-img-fallback");
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3 flex gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-md ${severityBadgeClass(plant.severity)}`}>
                        {plant.severity} Toxicity
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-black/60 backdrop-blur-md text-white">
                        {plant.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-bold font-serif text-[#2A2825] group-hover:text-[#B85D34] transition-colors">
                          {plant.name}
                        </h3>
                        <span className="text-xs font-bold px-2 py-1 bg-stone-100 rounded-md text-stone-700">
                          Score: {plant.toxicityScore}/10
                        </span>
                      </div>
                      <p className="text-xs italic text-stone-500 mt-0.5">{plant.scientificName}</p>
                      
                      <p className="text-xs text-stone-600 mt-3 line-clamp-2 leading-relaxed">
                        {plant.description}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
                      <div>
                        <strong className="text-stone-700">Toxin:</strong> {plant.toxin}
                      </div>
                      <span className="text-[#B85D34] font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        Details <ChevronRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredPlants.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-stone-300">
                <Leaf className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-stone-700">No toxic plants found</h3>
                <p className="text-sm text-stone-500 mt-1">Try adjusting your search query or filter criteria.</p>
                <button 
                  onClick={() => { setSearchQuery(""); setSelectedCategory("All"); setSelectedSeverity("All"); }}
                  className="mt-4 px-4 py-2 bg-[#3B5333] text-white rounded-xl text-xs font-semibold"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* ================= TAB 2: PASTURE SIMULATOR ================= */}
        {activeTab === "simulator" && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#3B5333] to-[#2D4027] text-white p-6 sm:p-8 rounded-3xl shadow-lg space-y-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-emerald-300 text-xs font-semibold">
                <Compass className="w-3.5 h-3.5" /> Interactive Pasture & Feed Audit
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif">Alpaca Pasture Safety Simulator</h2>
              <p className="text-stone-200 text-sm leading-relaxed">
                Select items currently present in your pasture, hay bales, or proposed treats to instantly analyze toxicity risks for your alpacas.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E3DCD0] shadow-sm space-y-6">
              <h3 className="text-lg font-bold font-serif text-[#2A2825] flex items-center gap-2">
                <Leaf className="w-5 h-5 text-[#3B5333]" /> Check Items Present in Pasture / Feed
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {simulatorItems.map((item, idx) => (
                  <label
                    key={item.id}
                    data-testid={`${APP.simulatorSelect}-${idx}`}
                    className={`flex items-start gap-3 p-4 rounded-2xl border transition-all cursor-pointer ${simulatorItemClass(item)}`}
                  >
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => {
                        const updated = [...simulatorItems];
                        updated[idx].checked = !updated[idx].checked;
                        setSimulatorItems(updated);
                      }}
                      className="mt-1 w-4 h-4 rounded text-[#3B5333] focus:ring-[#3B5333]"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-sm">{item.name}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          item.type === "toxic" ? "bg-red-200 text-red-800" : "bg-emerald-200 text-emerald-800"
                        }`}>
                          {item.type === "toxic" ? "Hazard" : "Safe Forage"}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Add custom item */}
              <div className="pt-4 border-t border-stone-100 flex gap-3">
                <input
                  type="text"
                  placeholder="Or type another plant/food item to test..."
                  value={customSimulatorInput}
                  onChange={(e) => setCustomSimulatorInput(e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333]"
                />
                <button
                  onClick={() => {
                    if (!customSimulatorInput) return;
                    const isToxicName = customSimulatorInput.toLowerCase().includes("rhododendron") || 
                                       customSimulatorInput.toLowerCase().includes("oak") ||
                                       customSimulatorInput.toLowerCase().includes("yew") ||
                                       customSimulatorInput.toLowerCase().includes("chocolate") ||
                                       customSimulatorInput.toLowerCase().includes("mold") ||
                                       customSimulatorInput.toLowerCase().includes("maple");
                    setSimulatorItems([
                      ...simulatorItems,
                      {
                        id: `custom_${Date.now()}`,
                        name: customSimulatorInput,
                        type: isToxicName ? "toxic" : "safe",
                        checked: true
                      }
                    ]);
                    setCustomSimulatorInput("");
                  }}
                  className="px-4 py-2.5 bg-stone-200 hover:bg-stone-300 text-stone-800 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add Item
                </button>
              </div>

              <div className="pt-4">
                <button
                  data-testid={APP.simulatorRunBtn}
                  onClick={runSimulator}
                  className="w-full py-4 bg-[#B85D34] hover:bg-[#9E4C27] text-white font-bold rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2 text-base"
                >
                  <Sparkles className="w-5 h-5 text-amber-200" /> Run Pasture Safety Audit
                </button>
              </div>

              {/* Simulation Result */}
              {simulatorResult && (
                <div className={`p-6 rounded-2xl border space-y-4 animate-in fade-in duration-300 ${
                  simulatorResult.toxicFound > 0 ? "bg-red-50 border-red-200 text-red-950" : "bg-emerald-50 border-emerald-200 text-emerald-950"
                }`}>
                  <div className="flex items-center gap-3">
                    {simulatorResult.toxicFound > 0 ? (
                      <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center font-bold">
                        <AlertTriangle className="w-6 h-6" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-lg">
                        {simulatorResult.toxicFound > 0 
                          ? `⚠️ Warning: ${simulatorResult.toxicFound} Toxic Hazard(s) Detected in Selection!`
                          : `✅ Pasture & Feed Check Passed: No Known Toxins Found`}
                      </h4>
                      <p className="text-xs opacity-80">
                        Audited {simulatorResult.totalChecked} items in current pasture environment.
                      </p>
                    </div>
                  </div>

                  {simulatorResult.toxicFound > 0 && (
                    <div className="space-y-2 pt-2 border-t border-red-200">
                      <p className="text-xs font-bold uppercase tracking-wider text-red-800">Detected Threats:</p>
                      <ul className="space-y-2">
                        {simulatorResult.items.map((item) => {
                          const plantData = toxicPlants.find(p => p.id === item.plantId);
                          return (
                            <li key={item.id} className="bg-white/80 p-3 rounded-xl border border-red-200 flex justify-between items-center text-xs">
                              <div>
                                <span className="font-bold text-red-900">{item.name}</span>
                                {plantData && <p className="text-stone-600 mt-0.5">Toxin: {plantData.toxin} — {plantData.symptoms[0]}</p>}
                              </div>
                              {plantData && (
                                <button 
                                  onClick={() => setSelectedPlant(plantData)}
                                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors"
                                >
                                  View Protocol
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Safe Forage & Diet Reference */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E3DCD0] shadow-sm space-y-4">
              <h3 className="text-lg font-bold font-serif text-[#2A2825] flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Safe Alpaca Forage Quick Reference
              </h3>
              <p className="text-xs text-stone-600">Always ensure fresh water and high quality low-calcium grass hay constitute 80%+ of your alpaca's diet.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {SAFE_ITEMS.map((safe) => (
                  <div key={safe.name} className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100 flex items-start gap-3">
                    <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-xs text-emerald-950">{safe.name}</h4>
                        <span className="text-[10px] bg-emerald-200 text-emerald-800 font-bold px-2 py-0.5 rounded">{safe.category}</span>
                      </div>
                      <p className="text-[11px] text-stone-600 mt-1">{safe.benefit}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= TAB 3: EMERGENCY CARE ================= */}
        {activeTab === "emergency" && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#B85D34] to-[#9E4C27] text-white p-6 sm:p-8 rounded-3xl shadow-lg space-y-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-amber-200 text-xs font-semibold">
                <HeartPulse className="w-3.5 h-3.5" /> Urgent Vet Protocol
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif">Alpaca Poisoning Emergency Action Plan</h2>
              <p className="text-stone-100 text-sm leading-relaxed">
                If you suspect your alpaca has ingested a toxic plant or poisonous foodstuff, time is critical. Follow this step-by-step veterinary emergency protocol.
              </p>
            </div>

            {/* Emergency Contacts */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E3DCD0] shadow-sm space-y-4">
              <h3 className="text-lg font-bold font-serif text-[#2A2825] flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-[#B85D34]" /> Emergency Hotlines & Veterinary Contacts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {VET_CONTACTS.map((vet, idx) => (
                  <div key={vet.name} className="p-4 bg-stone-50 rounded-2xl border border-stone-200 space-y-2 flex flex-col justify-between">
                    <div>
                      <h4 className="font-bold text-xs text-stone-900">{vet.name}</h4>
                      <p className="text-[11px] text-stone-500 mt-0.5">{vet.hours}</p>
                    </div>
                    <a
                      data-testid={idx === 0 ? APP.emergencyBtn : undefined}
                      href={`tel:${vet.phone.replace(/[^0-9]/g, '')}`}
                      className="inline-flex items-center justify-center gap-2 w-full py-2.5 bg-[#B85D34] text-white rounded-xl text-xs font-bold hover:bg-[#9E4C27] transition-colors"
                    >
                      <PhoneCall className="w-3.5 h-3.5" /> {vet.phone}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Step-by-Step Protocol */}
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#E3DCD0] shadow-sm space-y-6">
              <h3 className="text-lg font-bold font-serif text-[#2A2825]">Immediate First Aid Steps</h3>
              <div className="space-y-4">
                {EMERGENCY_STEPS.map(step => (
                  <div key={step.step} className="flex gap-4 p-4 rounded-2xl bg-stone-50 border border-stone-200">
                    <div className="w-10 h-10 rounded-xl bg-[#3B5333] text-white flex items-center justify-center font-bold text-base shrink-0 shadow-sm">
                      {step.step}
                    </div>
                    <div className="space-y-1">
                      <h4 className="font-bold text-sm text-[#2A2825]">{step.title}</h4>
                      <p className="text-xs text-stone-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= TAB 4: VET REPORTS ================= */}
        {activeTab === "reports" && (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-[#E3DCD0] shadow-sm">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#3B5333]/10 text-[#3B5333] text-xs font-semibold mb-2">
                  <UserCheck className="w-3.5 h-3.5" /> Community & Veterinarian Insights
                </span>
                <h2 className="text-2xl font-bold font-serif text-[#2A2825]">Field Toxicology Reports</h2>
                <p className="text-xs text-stone-600 mt-1">Real-world case studies and hazard warnings reported by camelid owners and veterinarians.</p>
              </div>
              <button
                data-testid="open-report-modal"
                onClick={() => setShowReportModal(true)}
                className="px-4 py-2.5 bg-[#3B5333] hover:bg-[#2D4027] text-white rounded-xl text-xs font-semibold flex items-center gap-2 shadow-md transition-all"
              >
                <Plus className="w-4 h-4" /> Share Vet / Farm Report
              </button>
            </div>

            <div className="space-y-4">
              {reports.map(report => (
                <div key={report.id} className="bg-white p-6 rounded-2xl border border-[#E3DCD0] shadow-sm space-y-3">
                  <div className="flex flex-wrap justify-between items-start gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-base text-[#2A2825]">{report.title}</h3>
                        {report.verified && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                            Vet Verified
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">By {report.author} • {report.location} ({report.date})</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                      report.severity === "Critical" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                    }`}>
                      {report.severity} Severity
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-700 leading-relaxed pt-2 border-t border-stone-100">
                    {report.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-[#2A2825] text-stone-400 py-10 px-4 sm:px-8 mt-12 border-t border-stone-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#3B5333] flex items-center justify-center text-white">
              <Leaf className="w-4 h-4 text-[#D4A373]" />
            </div>
            <span className="text-white font-bold font-serif text-lg">AlpacaSafe 2026</span>
          </div>
          <p className="text-xs text-center md:text-left">
            Designed for alpaca breeders, sanctuaries, and camelid veterinarians. Always consult your local livestock veterinarian for medical emergencies.
          </p>
          <div className="text-xs text-stone-500">
            Powered by Emergent Intelligence
          </div>
        </div>
      </footer>

      {/* ================= PLANT DETAIL MODAL ================= */}
      {selectedPlant && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div 
            data-testid={APP.plantDetailModal}
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-stone-200 animate-in fade-in zoom-in-95 duration-200"
          >
            <div className="relative h-64 sm:h-72 bg-stone-100">
              <img
                src={selectedPlant.image}
                alt={selectedPlant.name}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.opacity = "0";
                  e.currentTarget.parentElement?.classList.add("plant-img-fallback");
                }}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => setSelectedPlant(null)}
                  className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-md ${
                    selectedPlant.severity === "Critical" ? "bg-red-600 text-white" : "bg-amber-600 text-white"
                  }`}>
                    {selectedPlant.severity} Toxicity ({selectedPlant.toxicityScore}/10)
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-bold font-serif text-white mt-2 drop-shadow-md">
                    {selectedPlant.name}
                  </h2>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              <div className="flex flex-wrap gap-4 text-xs font-medium text-stone-600 pb-4 border-b border-stone-100">
                <div><strong>Scientific:</strong> <span className="italic">{selectedPlant.scientificName}</span></div>
                <div><strong>Category:</strong> {selectedPlant.category}</div>
                <div><strong>Season:</strong> {selectedPlant.season}</div>
              </div>

              <div className="space-y-2">
                <h4 className="font-bold text-sm text-[#2A2825]">Toxicology Profile</h4>
                <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">{selectedPlant.description}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-red-50 rounded-2xl border border-red-100 space-y-2">
                  <h5 className="font-bold text-xs text-red-900 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-red-600" /> Toxin & Parts Affected
                  </h5>
                  <p className="text-xs text-red-950"><strong>Toxin:</strong> {selectedPlant.toxin}</p>
                  <p className="text-xs text-red-950"><strong>Parts:</strong> {selectedPlant.partsAffected}</p>
                </div>

                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 space-y-2">
                  <h5 className="font-bold text-xs text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Safe Alternatives
                  </h5>
                  <p className="text-xs text-emerald-950">{selectedPlant.safeAlternative}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-bold text-sm text-[#2A2825]">Common Symptoms in Alpacas</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedPlant.symptoms.map((sym) => (
                    <span key={sym} className="px-3 py-1 rounded-xl bg-stone-100 text-stone-800 text-xs font-semibold">
                      • {sym}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 space-y-1">
                <h4 className="font-bold text-xs text-amber-900">Veterinary Treatment Protocol</h4>
                <p className="text-xs text-amber-950">{selectedPlant.treatment}</p>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setSelectedPlant(null)}
                  className="px-6 py-2.5 bg-[#3B5333] hover:bg-[#2D4027] text-white rounded-xl text-xs font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= REPORT MODAL ================= */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 space-y-6 shadow-2xl border border-stone-200 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold font-serif text-[#2A2825]">Share Vet / Farm Report</h3>
              <button onClick={() => setShowReportModal(false)} className="text-stone-400 hover:text-stone-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddReport} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-700 mb-1">Your Name / Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dr. Jane Doe, DVM"
                  value={newReport.author}
                  onChange={(e) => setNewReport({ ...newReport, author: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-700 mb-1">Farm / Location</label>
                <input
                  type="text"
                  placeholder="e.g. Meadow Alpaca Ranch, TX"
                  value={newReport.location}
                  onChange={(e) => setNewReport({ ...newReport, location: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">Report Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Acorn poisoning case"
                    value={newReport.title}
                    onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-700 mb-1">Severity Level</label>
                  <select
                    value={newReport.severity}
                    onChange={(e) => setNewReport({ ...newReport, severity: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333] bg-white"
                  >
                    <option value="Critical">Critical</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-stone-700 mb-1">Detailed Case Notes & Observations</label>
                <textarea
                  required
                  rows={4}
                  placeholder="Describe the incident, symptoms observed, plant involved, and veterinary outcome..."
                  value={newReport.content}
                  onChange={(e) => setNewReport({ ...newReport, content: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3B5333]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2.5 rounded-xl bg-stone-100 text-stone-700 text-xs font-semibold hover:bg-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 rounded-xl bg-[#3B5333] text-white text-xs font-semibold hover:bg-[#2D4027] shadow-md"
                >
                  Submit Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= PLANT SCANNER MODAL ================= */}
      {showScanModal && (
        <div
          data-testid="plant-scanner-modal"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeScanner(); }}
        >
          <div className="bg-[#F9F7F2] rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl border border-[#E3DCD0]">
            <div className="sticky top-0 bg-[#F9F7F2] px-6 py-4 border-b border-[#E3DCD0] flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-[#3B5333] text-[#D4A373] flex items-center justify-center">
                  <ScanLine className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-[#2A2825]">Plant Photo Risk Analysis</h3>
                  <p className="text-xs text-stone-500">Powered by Gemini 3 Vision · Alpaca-focused toxicology</p>
                </div>
              </div>
              <button
                data-testid="close-scanner-btn"
                onClick={closeScanner}
                className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600"
                aria-label="Close scanner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* --- INITIAL / CHOOSER STATE --- */}
              {!scanImagePreview && !cameraActive && !analysisResult && (
                <div className="space-y-4">
                  <p className="text-sm text-stone-600 leading-relaxed">
                    Take a clear photo of a suspicious plant or foodstuff, or upload one from your device. Our AI will estimate the risk to your alpacas — <span className="italic">not a replacement for a veterinarian</span>.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      data-testid="scan-open-camera-btn"
                      onClick={startCamera}
                      className="flex flex-col items-center justify-center gap-2 p-6 rounded-2xl border-2 border-dashed border-[#3B5333]/40 bg-[#EAF0E8] hover:bg-[#DDE7DA] transition-all text-[#3B5333]"
                    >
                      <Camera className="w-8 h-8" />
                      <span className="font-semibold text-sm">Use Camera</span>
                      <span className="text-xs text-stone-500">Live capture from your device</span>
                    </button>
                    <button
                      data-testid="scan-upload-btn"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className="flex flex-col items-center justify-center gap-2 p-6 rounded-2xl border-2 border-dashed border-[#B85D34]/40 bg-[#FBEDE4] hover:bg-[#F6DDCE] transition-all text-[#B85D34]"
                    >
                      <Upload className="w-8 h-8" />
                      <span className="font-semibold text-sm">Upload Photo</span>
                      <span className="text-xs text-stone-500">JPEG · PNG · WEBP</span>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="scan-file-input"
                    />
                  </div>
                </div>
              )}

              {/* --- CAMERA LIVE PREVIEW --- */}
              {cameraActive && (
                <div className="space-y-3">
                  <div className="relative rounded-2xl overflow-hidden bg-black aspect-video">
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      autoPlay
                      className="w-full h-full object-cover"
                      data-testid="scan-camera-video"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid="scan-capture-btn"
                      onClick={capturePhoto}
                      className="flex-1 py-2.5 rounded-xl bg-[#3B5333] text-white font-semibold text-sm hover:bg-[#2D4027]"
                    >
                      Capture Photo
                    </button>
                    <button
                      data-testid="scan-camera-cancel-btn"
                      onClick={stopCamera}
                      className="px-4 py-2.5 rounded-xl bg-stone-200 text-stone-700 font-semibold text-sm hover:bg-stone-300"
                    >
                      Cancel
                    </button>
                  </div>
                  <canvas ref={canvasRef} className="hidden" />
                </div>
              )}

              {/* --- IMAGE READY: ANALYZE / RETAKE --- */}
              {scanImagePreview && !analysisResult && (
                <div className="space-y-3">
                  <img
                    src={scanImagePreview}
                    alt="Selected plant"
                    className="w-full rounded-2xl border border-[#E3DCD0] max-h-[420px] object-contain bg-stone-100"
                    data-testid="scan-preview-image"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid="scan-analyze-btn"
                      onClick={analyzeImage}
                      disabled={analyzing}
                      className="flex-1 py-3 rounded-xl bg-[#B85D34] text-white font-semibold text-sm hover:bg-[#9E4C27] disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {analyzing ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Analyze for Alpaca Risk</>
                      )}
                    </button>
                    <button
                      data-testid="scan-retake-btn"
                      onClick={resetScan}
                      disabled={analyzing}
                      className="px-4 py-3 rounded-xl bg-stone-200 text-stone-700 font-semibold text-sm hover:bg-stone-300 disabled:opacity-60"
                    >
                      Retake
                    </button>
                  </div>
                </div>
              )}

              {/* --- ERROR --- */}
              {scanError && (
                <div data-testid="scan-error" className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                  <AlertOctagon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{scanError}</span>
                </div>
              )}

              {/* --- ANALYSIS RESULT (Multi-plant) --- */}
              {analysisResult && (
                <div data-testid="scan-result" className="space-y-3">
                  {scanImagePreview && (
                    <img src={scanImagePreview} alt="Analyzed plant" className="w-full rounded-2xl border border-[#E3DCD0] max-h-[260px] object-contain bg-stone-100" />
                  )}

                  {/* Overall risk banner */}
                  <div className={`rounded-2xl p-4 border-2 ${riskColorClasses(analysisResult.overall_risk_level)}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        {analysisResult.overall_risk_level && analysisResult.overall_risk_level.toLowerCase() === "safe"
                          ? <ShieldCheck className="w-5 h-5" />
                          : <AlertOctagon className="w-5 h-5" />}
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Overall Risk</div>
                          <div className="text-lg font-bold" data-testid="scan-risk-level">{analysisResult.overall_risk_level || "Unknown"}</div>
                        </div>
                      </div>
                      <div className="text-xs opacity-90 font-medium">
                        {(analysisResult.plants?.length || 0)} plant{(analysisResult.plants?.length || 0) === 1 ? "" : "s"} detected
                      </div>
                    </div>
                    {analysisResult.summary && (
                      <p className="text-sm mt-2 opacity-95 leading-relaxed">{analysisResult.summary}</p>
                    )}
                  </div>

                  {/* Per-plant cards */}
                  {(analysisResult.plants || []).length === 0 && (
                    <div className="bg-white rounded-2xl border border-[#E3DCD0] p-4 text-sm text-stone-600" data-testid="scan-no-plants">
                      No individual plants could be identified. Try a closer, well-lit photo focused on leaves and stems.
                    </div>
                  )}

                  {(analysisResult.plants || []).map((p, idx) => (
                    <div
                      key={`${p.common_name || 'unknown'}-${idx}`}
                      data-testid={`scan-plant-${idx}`}
                      className="bg-white rounded-2xl border border-[#E3DCD0] p-4 space-y-3 text-sm"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-bold text-stone-500">#{idx + 1}</span>
                            <div className="text-base font-semibold text-[#2A2825]" data-testid={`scan-plant-${idx}-name`}>
                              {p.common_name || "Unknown plant"}
                            </div>
                            {p.scientific_name && (
                              <span className="italic text-stone-500 text-xs">({p.scientific_name})</span>
                            )}
                          </div>
                          <div className="text-[11px] text-stone-500 mt-0.5">
                            Confidence: <span className="font-semibold uppercase">{p.confidence}</span>
                            {" · "}
                            {p.toxic_to_alpacas ? "Toxic to alpacas" : "Not classified as toxic"}
                          </div>
                        </div>
                        <span className={`px-2.5 py-1 rounded-lg text-[11px] font-bold uppercase tracking-wider border ${riskColorClasses(p.risk_level)}`} data-testid={`scan-plant-${idx}-risk`}>
                          {p.risk_level}
                        </span>
                      </div>

                      {p.affected_parts && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Affected Parts</div>
                          <div className="text-sm text-stone-700">{p.affected_parts}</div>
                        </div>
                      )}

                      {p.toxins && p.toxins.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Toxins</div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {p.toxins.map((t) => (
                              <span key={t} className="px-2 py-0.5 rounded-lg bg-red-50 text-red-700 text-xs border border-red-200">{t}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {p.symptoms && p.symptoms.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Possible Symptoms</div>
                          <ul className="list-disc list-inside text-sm text-stone-700 space-y-0.5 mt-1">
                            {p.symptoms.map((s) => <li key={s}>{s}</li>)}
                          </ul>
                        </div>
                      )}

                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Recommendation</div>
                        <p className="text-sm text-stone-700 leading-relaxed">{p.recommendation}</p>
                      </div>

                      {p.matched_plant_id && (
                        <button
                          data-testid={`scan-plant-${idx}-view-btn`}
                          onClick={() => {
                            const match = toxicPlants.find(pl => pl.id === p.matched_plant_id);
                            if (match) {
                              closeScanner();
                              setSelectedPlant(match);
                            }
                          }}
                          className="w-full py-2 rounded-xl bg-[#3B5333] text-white text-sm font-semibold hover:bg-[#2D4027] flex items-center justify-center gap-2"
                        >
                          <BookOpen className="w-4 h-4" /> View in Encyclopedia
                        </button>
                      )}
                    </div>
                  ))}

                  <div className="text-xs text-stone-500 italic text-center px-2">{analysisResult.disclaimer}</div>

                  <button
                    data-testid="scan-again-btn"
                    onClick={resetScan}
                    className="w-full py-2.5 rounded-xl bg-stone-200 text-stone-700 font-semibold text-sm hover:bg-stone-300"
                  >
                    Scan Another Plant
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
